import type { BackendConfig } from '../../config'
import { BackendError, backendErrorFromResponse, backendErrorFromUnknown } from '../../errors'

export interface RequestOptions {
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | undefined>
  headers?: Record<string, string>
}

export interface Transport {
  readonly config: BackendConfig
  request(method: string, path: string, opts?: RequestOptions): Promise<Response>
  json<T>(method: string, path: string, opts?: RequestOptions): Promise<T>
}

export function createTransport(config: BackendConfig): Transport {
  const doFetch = config.fetch ?? globalThis.fetch
  const timeoutMs = config.timeoutMs ?? 30_000

  async function request(
    method: string,
    path: string,
    opts: RequestOptions = {},
  ): Promise<Response> {
    const url = new URL(path, config.baseUrl)
    if (opts.query) {
      for (const [k, v] of Object.entries(opts.query)) {
        if (v !== undefined) url.searchParams.set(k, String(v))
      }
    }

    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
      timeoutMs,
    )
    const onAbort = () => controller.abort()
    if (opts.signal) {
      if (opts.signal.aborted) controller.abort()
      else opts.signal.addEventListener('abort', onAbort, { once: true })
    }

    try {
      const token = await config.getAccessToken()
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        ...opts.headers,
      }
      let body: BodyInit | undefined
      if (opts.body instanceof FormData) {
        body = opts.body
      } else if (opts.body !== undefined) {
        headers['Content-Type'] = 'application/json'
        body = JSON.stringify(opts.body)
      }

      const res = await doFetch(url, { method, headers, body, signal: controller.signal })
      if (!res.ok) throw await backendErrorFromResponse(res)
      return res
    } catch (e) {
      const err = e instanceof BackendError ? e : backendErrorFromUnknown(e)
      config.onError?.(err)
      throw err
    } finally {
      clearTimeout(timer)
      opts.signal?.removeEventListener('abort', onAbort)
    }
  }

  async function json<T>(method: string, path: string, opts?: RequestOptions): Promise<T> {
    const res = await request(method, path, opts)
    return (await res.json()) as T
  }

  return { config, request, json }
}

/** Parse an SSE response body into its `data:` payloads. */
export async function* parseSse(res: Response): AsyncIterable<string> {
  const body = res.body
  if (!body) return
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let sep: number
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)
        const data = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('\n')
        if (data) yield data
      }
    }
  } finally {
    reader.releaseLock()
  }
}
